import { useState, useEffect } from 'react';
import { useSettings, useUpdateSettings } from '../../hooks/useSettings.js';
import { useError } from '../../components/shared/ErrorBanner.jsx';
import { post } from '../../api/client.js';

// One screen, three sections. Each section has its own Save button so a
// partial change doesn't require submitting the whole config.
export default function SettingsPage() {
  const { data, isLoading } = useSettings();
  if (isLoading || !data) return <div className="loading">Loading…</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <CompanySection settings={data.settings} />
      <NotificationsSection settings={data.settings} />
      <PayrollDefaultsSection settings={data.settings} />
      <TaxOnboardingPlaceholder settings={data.settings} />
      <MaintenanceSection />
    </div>
  );
}

// ─── Section: Maintenance ─────────────────────────────────────────────────
// Ops actions that don't fit elsewhere. Today: re-register Discord slash
// commands (replaces the shell-script flow with a Pages-Function call
// using the existing DISCORD_CLIENT_ID + DISCORD_BOT_TOKEN secrets).
function MaintenanceSection() {
  const { show } = useError();
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState(null);

  const reregister = async () => {
    if (!confirm('Re-register the bot\'s slash commands with Discord?')) return;
    setPending(true);
    setResult(null);
    try {
      const body = await post('/api/admin/discord/register-commands');
      setResult({ ok: true, ...body });
    } catch (err) {
      setResult({ ok: false, message: err.message, body: err.body });
      show(err.message);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="panel">
      <h3 className="panel-title">
        <span><i className="ph ph-wrench" /> Maintenance</span>
      </h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 13, color: 'var(--offwhite)', marginBottom: 4 }}>
            Re-register Discord slash commands
          </div>
          <Hint>
            Push the current command set (defined in <code>functions/api/_discord-commands.js</code>) to Discord.
            Re-run after editing the command list or after deploying any change to <code>/clockin</code> or <code>/clockout</code> options.
          </Hint>
        </div>
        <button className="btn-sm primary" type="button" onClick={reregister} disabled={pending}>
          {pending ? 'Registering…' : 'Re-register'}
        </button>
      </div>
      {result && result.ok && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--success, #4ade80)' }}>
          ✓ Registered {result.count} commands {result.scope === 'guild' ? `to guild ${result.guildId}` : 'globally (may take up to an hour to propagate)'}.
        </div>
      )}
      {result && !result.ok && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--error)' }}>
          ✗ {result.message}
        </div>
      )}
    </div>
  );
}

// ─── Section: Company ─────────────────────────────────────────────────────
function CompanySection({ settings }) {
  const [legalName, setLegalName] = useState(settings.company.legal_name);
  const [ein, setEin] = useState(settings.company.ein);
  const [contactEmail, setContactEmail] = useState(settings.company.contact_email);
  const [defaultState, setDefaultState] = useState(settings.company.default_state);
  const [street, setStreet] = useState(settings.company.address.street);
  const [city, setCity] = useState(settings.company.address.city);
  const [state, setState] = useState(settings.company.address.state);
  const [zip, setZip] = useState(settings.company.address.zip);
  const update = useUpdateSettings();
  const { show } = useError();

  const save = async (e) => {
    e.preventDefault();
    try {
      await update.mutateAsync({
        company: {
          legal_name: legalName,
          ein,
          contact_email: contactEmail,
          default_state: defaultState,
          address: { street, city, state, zip },
        },
      });
    } catch (err) { show(err.message); }
  };

  return (
    <Card title="Company" icon="ph-buildings" onSubmit={save} saving={update.isPending}>
      <Grid2>
        <Field label="Legal name"><input className="field-input" value={legalName} onChange={e => setLegalName(e.target.value)} /></Field>
        <Field label="EIN"><input className="field-input" value={ein} onChange={e => setEin(e.target.value)} placeholder="12-3456789" /></Field>
        <Field label="Contact email"><input className="field-input" type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} /></Field>
        <Field label="Default state"><input className="field-input" value={defaultState} onChange={e => setDefaultState(e.target.value)} placeholder="e.g. TX" maxLength={2} /></Field>
      </Grid2>
      <Field label="Street"><input className="field-input" value={street} onChange={e => setStreet(e.target.value)} /></Field>
      <Grid3>
        <Field label="City"><input className="field-input" value={city} onChange={e => setCity(e.target.value)} /></Field>
        <Field label="State"><input className="field-input" value={state} onChange={e => setState(e.target.value)} maxLength={2} /></Field>
        <Field label="ZIP"><input className="field-input" value={zip} onChange={e => setZip(e.target.value)} /></Field>
      </Grid3>
    </Card>
  );
}

// ─── Section: Notifications ───────────────────────────────────────────────
function NotificationsSection({ settings }) {
  const [jhaChannel, setJhaChannel] = useState(settings.notifications.jha_digest_channel_id);
  const [expiryChannel, setExpiryChannel] = useState(settings.notifications.expiry_channel_id);
  const [stuckChannel, setStuckChannel] = useState(settings.notifications.stuck_shift_channel_id || '');
  const update = useUpdateSettings();
  const { show } = useError();

  const save = async (e) => {
    e.preventDefault();
    try {
      await update.mutateAsync({
        notifications: {
          jha_digest_channel_id: jhaChannel,
          expiry_channel_id: expiryChannel,
          stuck_shift_channel_id: stuckChannel,
        },
      });
    } catch (err) { show(err.message); }
  };
  return (
    <Card title="Notifications" icon="ph-bell" onSubmit={save} saving={update.isPending}>
      <Field label="Discord channel — daily JHA digest">
        <ChannelInput value={jhaChannel} onChange={setJhaChannel} testPath="/api/admin/notifications/jha-digest" />
      </Field>
      <Field label="Discord channel — doc expiry warnings">
        <input className="field-input" value={expiryChannel} onChange={e => setExpiryChannel(e.target.value)} placeholder="channel ID" />
      </Field>
      <Field label="Discord channel — stuck shift digest (optional)">
        <input className="field-input" value={stuckChannel} onChange={e => setStuckChannel(e.target.value)} placeholder="channel ID — when set, replaces DMs to the escalation chain" />
      </Field>
      <Hint>Expiry tiers fire at 30, 14, 7, and 1 days before expiry. Save before testing — the test buttons read the stored value.</Hint>
    </Card>
  );
}

// Channel id field + a "Test" button that POSTs with dry_run=1 to render
// the formatted preview without sending. Saves an "is this channel id
// going to work?" round-trip through Discord that an admin would
// otherwise have to do manually.
function ChannelInput({ value, onChange, testPath }) {
  const { show } = useError();
  const [pending, setPending] = useState(false);
  const [preview, setPreview] = useState(null);
  const onTest = async () => {
    setPending(true);
    setPreview(null);
    try {
      const body = await post(testPath + '?dry_run=1');
      setPreview(body.preview || JSON.stringify(body));
    } catch (err) {
      show(err.message);
    } finally {
      setPending(false);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="field-input" style={{ flex: 1 }} value={value}
          onChange={e => onChange(e.target.value)} placeholder="channel ID" />
        <button className="btn-sm" type="button" onClick={onTest} disabled={pending}>
          {pending ? 'Loading…' : 'Preview'}
        </button>
      </div>
      {preview && (
        <pre style={{
          background: 'var(--surface-container)', padding: 8,
          fontSize: 11, fontFamily: 'ui-monospace, monospace',
          whiteSpace: 'pre-wrap', margin: 0,
          maxHeight: 200, overflow: 'auto',
        }}>{preview}</pre>
      )}
    </div>
  );
}

// ─── Section: Payroll defaults ────────────────────────────────────────────
function PayrollDefaultsSection({ settings }) {
  const [otThr, setOtThr] = useState(settings.payroll.default_ot_threshold_hrs);
  const [otMult, setOtMult] = useState(settings.payroll.default_ot_multiplier);
  const [cadence, setCadence] = useState(settings.payroll.cadence);
  const [provider, setProvider] = useState(settings.payroll.default_provider);
  const [mileage, setMileage] = useState(settings.expenses.mileage_rate_cents_per_mile);
  const update = useUpdateSettings();
  const { show } = useError();
  const save = async (e) => {
    e.preventDefault();
    try {
      await update.mutateAsync({
        payroll: {
          default_ot_threshold_hrs: Number(otThr),
          default_ot_multiplier: Number(otMult),
          cadence,
          default_provider: provider,
        },
        expenses: { mileage_rate_cents_per_mile: Number(mileage) },
      });
    } catch (err) { show(err.message); }
  };
  return (
    <Card title="Payroll defaults" icon="ph-sliders" onSubmit={save} saving={update.isPending}>
      <Grid3>
        <Field label="OT threshold (hrs/wk)"><input className="field-input" type="number" step="1" value={otThr} onChange={e => setOtThr(e.target.value)} /></Field>
        <Field label="OT multiplier"><input className="field-input" type="number" step="0.1" value={otMult} onChange={e => setOtMult(e.target.value)} /></Field>
        <Field label="Cadence">
          <select className="field-select" value={cadence} onChange={e => setCadence(e.target.value)}>
            <option value="weekly">weekly</option>
            <option value="biweekly">biweekly</option>
            <option value="semi-monthly">semi-monthly</option>
          </select>
        </Field>
      </Grid3>
      <Grid2>
        <Field label="Default provider">
          <select className="field-select" value={provider} onChange={e => setProvider(e.target.value)}>
            <option value="manual">manual</option>
            <option value="nacha">NACHA ACH file</option>
            <option value="check">Check (preview)</option>
            <option value="gusto">Gusto</option>
          </select>
        </Field>
        <Field label="Mileage reimbursement (¢/mile)"><input className="field-input" type="number" step="1" value={mileage} onChange={e => setMileage(e.target.value)} /></Field>
      </Grid2>
      <Hint>Defaults apply to new hires only — existing rates are unchanged.</Hint>
    </Card>
  );
}

// ─── Section: Tax & onboarding (placeholder) ──────────────────────────────
function TaxOnboardingPlaceholder() {
  return (
    <div className="panel" style={{ background: 'var(--surface-container)', borderLeft: '3px solid var(--orange)' }}>
      <h3 className="panel-title" style={{ marginBottom: 8 }}>
        <span><i className="ph ph-link-simple-horizontal" /> Tax & payroll provider</span>
      </h3>
      <div style={{ fontSize: 13, color: 'var(--on-surface-variant)', lineHeight: 1.5 }}>
        Connect a payroll provider to automate tax filings, withholdings, and
        direct-deposit payouts. Until then, runs export as CSV / NACHA for
        manual processing.
      </div>
      <div style={{ marginTop: 12, fontSize: 12, color: 'var(--warm-gray)' }}>
        Recommended for 1099-only crews: Stripe Connect Express (auto 1099-NEC, W-9, ACH).<br />
        For W-2 employees: Check or Gusto Embedded.
      </div>
    </div>
  );
}

// ─── Reusable bits ────────────────────────────────────────────────────────
function Card({ title, icon, onSubmit, saving, children }) {
  return (
    <form className="panel" onSubmit={onSubmit}>
      <h3 className="panel-title">
        <span><i className={'ph ' + icon} /> {title}</span>
        <button className="btn-sm primary" type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</div>
    </form>
  );
}
function Field({ label, children }) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}
function Grid2({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>{children}</div>;
}
function Grid3({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>{children}</div>;
}
function Hint({ children }) {
  return (
    <div style={{ fontSize: 11, color: 'var(--warm-gray)', marginTop: 4 }}>{children}</div>
  );
}
